import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  IdentityApiService,
  TokenStore,
  type LoginResponseData,
  type MeResponseData,
} from '@vendorhub/shared/data-access';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, of, pipe, switchMap, tap } from 'rxjs';

interface AuthState {
  user: MeResponseData | null;
  loading: boolean;
  error: string | null;
  /** True after the first /me attempt has settled (success or failure). */
  initialized: boolean;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  initialized: false,
};

/**
 * Central auth state. Owns the authenticated user profile; token storage
 * is delegated to `TokenStore`. Guards and UI subscribe to the computed
 * signals (`isAuthenticated`, `roles`, `isFullyVerified`, ...).
 */
export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => ({
    isAuthenticated: computed(() => !!state.user()),
    publicId: computed(() => state.user()?.publicId ?? null),
    displayName: computed(() => state.user()?.displayName ?? null),
    email: computed(() => state.user()?.email ?? null),
    roles: computed(() => state.user()?.roles?.map((r) => r.code) ?? []),
    primaryRole: computed(() => state.user()?.roles?.find((r) => r.isPrimary)?.code ?? null),
    isEmailVerified: computed(() => state.user()?.isEmailVerified ?? false),
    isPhoneVerified: computed(() => state.user()?.isPhoneVerified ?? false),
    isFullyVerified: computed(
      () => (state.user()?.isEmailVerified ?? false) && (state.user()?.isPhoneVerified ?? false),
    ),
    accountStatus: computed(() => state.user()?.accountStatus ?? null),
    mustChangePassword: computed(() => state.user()?.mustChangePassword ?? false),
  })),
  withMethods((store) => {
    const tokenStore = inject(TokenStore);
    const api = inject(IdentityApiService);
    const router = inject(Router);

    const fetchMe = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        switchMap(() =>
          api.me().pipe(
            tap((user) => patchState(store, { user, loading: false, initialized: true })),
            catchError(() => {
              patchState(store, { user: null, loading: false, initialized: true });
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    const logout = rxMethod<void>(
      pipe(
        switchMap(() => api.logout().pipe(catchError(() => of(void 0)))),
        tap(() => {
          tokenStore.clear();
          patchState(store, initialState);
          void router.navigate(['/auth/login']);
        }),
      ),
    );

    return {
      fetchMe,
      logout,

      /** Called after login / phone-otp-login to persist tokens and fetch the profile. */
      onLoginSuccess(data: LoginResponseData): void {
        tokenStore.set(data.accessToken, data.expiresIn, data.refreshToken);
        fetchMe();
      },

      /** Token already set elsewhere (e.g. phone-otp login path). Just fetch /me. */
      hydrate(): void {
        fetchMe();
      },

      /** Clear state without calling /logout (used by error interceptor on refresh failure). */
      clearSession(): void {
        tokenStore.clear();
        patchState(store, { ...initialState, initialized: true });
      },

      /** Patch the locally-cached user after a verification step. */
      patchUser(update: Partial<MeResponseData>): void {
        const current = store.user();
        if (!current) return;
        patchState(store, { user: { ...current, ...update } });
      },
    };
  }),
);

export type AuthStoreInstance = InstanceType<typeof AuthStore>;
